import type { Resource } from 'i18next';
import { catalog, defaultNS } from './catalog.ts';
import { isTranslationBranch, isTranslationLeaf, type TranslationNode } from './key-paths.ts';
import { fallbackLanguage, supportedLanguages } from './languages.ts';
import enGBOverrides from './overrides/en-GB.json' with { type: 'json' };

export const namespaces = Object.keys(catalog);

const mergeTranslations = (base: TranslationNode, override: TranslationNode): TranslationNode => {
  if (isTranslationLeaf(override)) {
    return override;
  }

  if (!isTranslationBranch(base)) {
    return override;
  }

  const merged = { ...base };

  for (const [key, value] of Object.entries(override)) {
    const existing = base[key];
    merged[key] = existing !== undefined ? mergeTranslations(existing, value) : value;
  }

  return merged;
};

const enGBOverridesByNamespace: Record<string, TranslationNode | undefined> = enGBOverrides;

/**
 * The catalog is keyed namespace-first so each domain owns its locales in one
 * place; i18next wants locale-first, so transpose it here rather than making
 * every new namespace pay for a second hand-written shape. For en-GB, hand-
 * written overrides are deep-merged on top of Lingo's catalog so product-
 * decided terms and Lingo misses win per leaf.
 */
const buildResources = (): Resource => {
  const byLanguage: Record<string, Record<string, TranslationNode>> = {};

  for (const language of supportedLanguages) {
    const byNamespace: Record<string, TranslationNode> = {};

    for (const [namespace, translations] of Object.entries(catalog)) {
      const base = translations[language];
      const override = language === 'en-GB' ? enGBOverridesByNamespace[namespace] : undefined;
      byNamespace[namespace] = override ? mergeTranslations(base, override) : base;
    }

    byLanguage[language] = byNamespace;
  }

  return byLanguage;
};

export const resources = buildResources();

/** Shared i18next init options, spread by every entrypoint that builds an instance. */
export const i18nextSharedOptions = {
  defaultNS,
  ns: namespaces,
  // remix-i18next middleware calls `init({ lng })` only, so fallback must live in
  // the shared options object or empty target locales surface raw key paths.
  fallbackLng: fallbackLanguage,
  // React escapes interpolated values already. Leaving i18next's escaping on
  // double-encodes them, so a venue group called "Smith & Sons" renders as
  // "Smith &amp; Sons".
  interpolation: { escapeValue: false },
} as const;
